import {CachedSession} from '@/state/authAtoms.ts';

export function getInitials(cachedSession: CachedSession | null) {
   let firstInitial = '';
   let lastInitial = '';

   if (cachedSession?.firstName) {
      firstInitial = cachedSession.firstName[0].toUpperCase();
   }
   if (cachedSession?.lastName) {
      lastInitial = cachedSession.lastName[0].toUpperCase();
   }
   if (!firstInitial && cachedSession?.username) {
      firstInitial = cachedSession.username[0].toUpperCase();
   }
   return `${firstInitial}${lastInitial}`;
}

// crypto.randomUUID() only exists in "secure contexts" (HTTPS, or localhost).
// On a test/staging site opened over plain HTTP (or by raw IP), the browser
// still exposes `crypto` but not `crypto.randomUUID`, which throws
// "crypto.randomUUID is not a function" the moment any form tries to seed a
// new id. This wrapper falls back to a manual RFC4122-ish v4 UUID in that case.
export function generateUUID(): string {
   if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
   }
   return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
   });
}

/** ADDED 2026-09-15 (Duane): "For DailyReport we really shouldn't need a
 * separate record for marking a well down and a separate one for well
 * up. There needs only to be one record per day." Deterministic,
 * non-cryptographic 128-bit hash of `seed`, formatted as a UUID
 * v4-shaped string (version/variant nibbles forced the same way
 * generateUUID()'s real random output is, so a backend UUID! scalar
 * accepts it identically) — the SAME seed always produces the SAME id;
 * this is the opposite of generateUUID() above on purpose. Used by
 * dailyReportTypes.ts's toWellStatusReportInput to derive a stable id
 * for "the one DailyWellReport row that represents well X's up/down
 * status on day Y" — so that even if the caller's own existing-report
 * lookup misses (a stale query result, or two status flips queued
 * offline back-to-back before either ever synced), a second write for
 * the same (well, day) still resolves to the SAME id instead of
 * minting a fresh random one, and the backend's CreateUpdate
 * (upsert-by-id) updates that one row rather than ever creating a
 * second. Not a general-purpose hash — only for deriving a stable id
 * over an input space (wellId+date pairs) that's already effectively
 * unique in this app's own data, not for anything needing real
 * collision resistance against adversarial input. */
export function deterministicUuidFromSeed(seed: string): string {
   // Four independent 32-bit FNV-1a-style hashes (different starting
   // seeds, same prime) combined into a 128-bit value.
   const seeds = [0x811c9dc5, 0x1000193, 0x9e3779b9, 0x85ebca6b];
   const raw = seeds
      .map((seedValue) => {
         let h = seedValue;
         for (let i = 0; i < seed.length; i++) {
            h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
         }
         return (h >>> 0).toString(16).padStart(8, '0');
      })
      .join(''); // 32 hex chars
   const versionNibble = '4';
   const variantNibble = '89ab'[parseInt(raw[16], 16) & 0x3];
   return [
      raw.slice(0, 8),
      raw.slice(8, 12),
      versionNibble + raw.slice(13, 16),
      variantNibble + raw.slice(17, 20),
      raw.slice(20, 32),
   ].join('-');
}
